import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC variant of the Slack OAuth code exchange (COI-705), the V3 replacement
 * for the legacy `POST /slack/callback` rest-api handler. `useSlack` picks
 * between this and the legacy axios call on the `trpc` feature flag.
 *
 * `state` is passed through for parity with the legacy request body — the nonce
 * itself is verified in the browser before this runs (see `useSlack`).
 *
 * The Slack connection read (`slack.getConnection`) is invalidated on success.
 * The callback page runs in the popup Slack redirects to, so nothing there is
 * showing that query — but the invalidation is free and keeps the hook correct
 * if it is ever called from the settings modal itself.
 */
export const useConnectSlackTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    trpc.frontend.notifications.slack.connect.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.notifications.slack.getConnection.queryKey(),
        });
      },
    })
  );

  // Only the mutate fn is exposed: the sole caller (`useSlack`) returns a
  // boolean and drives the callback page's own loading/failure states, so a
  // `loading`/`error` surface here would be dead weight.
  return {
    connectSlack: async (code: string, state: string): Promise<void> => {
      await mutation.mutateAsync({ code, state });
    },
  };
};
